import { Skeleton, useComputedColorScheme } from "@mantine/core";
import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import {
  ArrowRight,
  CircleUserRound,
  Copy,
  Film,
  Image as ImageIcon,
  type LucideIcon,
  RotateCw,
} from "lucide-react";
import { type ReactNode, useLayoutEffect, useRef, useState } from "react";

import { useGetCatalog, useListTemplates } from "../../../api/generated/endpoints/generation/generation";
import { useGetMe } from "../../../api/generated/endpoints/identity/identity";
import { useListBrands, useListProducts } from "../../../api/generated/endpoints/teams/teams";
import type { Product, TemplateCard } from "../../../api/generated/model";
import { accessScopeQuery } from "../../../api/session";
import { freshUrls } from "../../../api/signed-urls";
import { ErrorAlert } from "../../../platform/ErrorAlert";
import { GenerationDetailModal } from "../../../platform/jobs/detail/GenerationDetailModal";
import { useJobList } from "../../../platform/jobs/job-list";
import { modelTitle } from "../../../platform/jobs/model-name";
import { useStreamStatus } from "../../../platform/stream-status";
import { TemplateCover } from "../../../ui/TemplateCover";
import { continueJobs } from "../ContinueSection";
import { featuredTemplates } from "../ProHomeScreen";
import home from "../pro-home.module.css";
import { ResultCard } from "../ResultCard";
import { SoonControl } from "../Soon";
import { backgroundBehind, brandColor, firstName, readsOn, withoutBrand } from "./greeting";
import {
  AD_STATIC_TYPES,
  Art,
  type Picture,
  PRODUCT_VIDEO_TYPES,
  TALKING_TYPES,
  useOwnPictures,
} from "./home-pictures";
import classes from "./simple-home.module.css";
import { TEMPLATE_READ } from "./TemplateCards";

const ROUTE = "/signed-in/teams/$teamId/brands/$brandId/studio/simple/";
const NOTHING = () => undefined;

/** The brand Simple is on, from the brands the header's menu reads. */
function useBrand(teamId: string, brandId: string) {
  return useListBrands(teamId, {
    query: { select: (list) => list.find((each) => each.id.toLowerCase() === brandId.toLowerCase()) },
  }).data;
}

/**
 * The brand's name in its own colour where that reads at 3:1 on the page, as the prototype's is,
 * else in the theme's primary (spec ui-perf/simple-flows).
 */
function BrandName({ name, color }: { name: string; color: string | undefined }) {
  const ref = useRef<HTMLSpanElement>(null);
  // Read again when the scheme flips: the page's colour is another then.
  const scheme = useComputedColorScheme("light");
  const [reads, setReads] = useState(false);
  useLayoutEffect(() => {
    setReads(color !== undefined && readsOn(color, backgroundBehind(ref.current)));
  }, [color, scheme]);
  return (
    <span ref={ref} className={classes.brandName} style={reads ? { color } : undefined}>
      {name}
    </span>
  );
}

function Greeting({ teamId, brandId }: { teamId: string; brandId: string }) {
  const first = useGetMe({ query: { select: (me) => firstName(me.name) } });
  const brand = useBrand(teamId, brandId);
  return (
    <header className={classes.greeting}>
      {brand === undefined || first.isPending ? (
        <Skeleton className={classes.helloSkeleton} />
      ) : (
        <h1 className={classes.hello}>
          {first.data === undefined ? "Hi, " : `Hi ${first.data}, `}what should we make for{" "}
          <BrandName name={brand.name} color={brandColor(brand)} />?
        </h1>
      )}
      <p className={classes.lead}>
        Pick what you need. We write it, cast it and set it up. You choose from ready options at each
        step.
      </p>
    </header>
  );
}

type Own = ReturnType<typeof useOwnPictures>;

interface Pictures {
  talking: Own;
  adStatic: Own;
  productVideo: Own;
}

/**
 * The prototype's three, two videos with a person and an image ad, each in its shape: the brand's
 * newest talking head and ad static, then the next talking head, else its newest product video.
 */
function startFrames({ talking, adStatic, productVideo }: Pictures) {
  const third = talking.pictures[1] === undefined ? productVideo : talking;
  const frames: readonly [Picture | undefined, Own, LucideIcon, "portrait" | "feed"][] = [
    [talking.pictures[0], talking, CircleUserRound, "portrait"],
    [adStatic.pictures[0], adStatic, ImageIcon, "feed"],
    [talking.pictures[1] ?? productVideo.pictures[0], third, Film, "portrait"],
  ];
  return frames;
}

/**
 * The newest product's name. The products' default read is A to Z (the API's default order), so
 * the newest is the latest made, then the latest id, as two can share a moment.
 */
function newestName(list: readonly Product[]): string | undefined {
  const newest = [...list].sort(
    (one, other) => other.created_at.localeCompare(one.created_at) || other.id.localeCompare(one.id),
  )[0];
  return newest?.name;
}

/** The prototype's "Start here": a set of five different ads. Not built yet ("Small new"). */
function StartHere(props: { teamId: string; brandId: string; pictures: Pictures }) {
  const { teamId, brandId, pictures } = props;
  const brand = useBrand(teamId, brandId)?.name ?? "";
  // The brand's newest product, from the products' default read (shared with the guided page's
  // step and its cache).
  const product = useListProducts(teamId, brandId, undefined, { query: { select: newestName } }).data;
  const title = `Get 5 new ads for ${product === undefined ? brand : withoutBrand(product, brand)}`;
  return (
    <SoonControl block className={classes.start} label={title}>
      <span className={classes.startPictures}>
        {startFrames(pictures).map(([picture, own, icon, shape], index) => (
          <span key={index} className={`${classes.startPicture} ${classes[shape]}`}>
            <Art picture={picture} icon={icon} onLost={own.readAgain} />
          </span>
        ))}
      </span>
      <span className={classes.startWords}>
        <span className={classes.startTag}>Start here</span>
        <span className={classes.startTitle}>{title}</span>
        <span className={classes.startText}>
          5 different ideas in one go: 2 videos with a person, 1 product video and 2 image ads.
          Different enough that Meta treats each one as its own ad.
        </span>
        <span className={classes.startCta}>
          Get my 5 ads <ArrowRight size={16} aria-hidden />
        </span>
      </span>
    </SoonControl>
  );
}

type Way = "ugc" | "image-ad" | "product-video" | "soon";

interface OneAd {
  title: string;
  line: string;
  icon: LucideIcon;
  way: Way;
}

/** The prototype's `ONE`, in its order and words. */
const ONE_AD: readonly OneAd[] = [
  { title: "UGC video ad", line: "A person talks about your product to the camera", icon: CircleUserRound, way: "ugc" },
  { title: "Image ad", line: "Your product with a headline and a button", icon: ImageIcon, way: "image-ad" },
  {
    title: "Product video",
    line: "Your product photo turned into a short clip with sound",
    icon: Film,
    way: "product-video",
  },
  // Needs the ads area's inspiration (#253).
  { title: "Copy a winning ad", line: "Pick a live ad from another brand and make it yours", icon: Copy, way: "soon" },
  // A "Small new" wizard, its own PR.
  {
    title: "More like your best ad",
    line: "Same idea with a new person, opening line or setting",
    icon: RotateCw,
    way: "soon",
  },
];

function Card(props: { card: OneAd; own: Own | undefined; teamId: string; brandId: string }) {
  const { card, own, teamId, brandId } = props;
  const params = { teamId, brandId };
  const body: ReactNode = (
    <>
      <span className={classes.cardArt}>
        <Art picture={own?.pictures[0]} icon={card.icon} onLost={own?.readAgain ?? NOTHING} />
      </span>
      <span className={classes.cardBody}>
        <b className={classes.cardTitle}>{card.title}</b>
        <span className={classes.cardLine}>{card.line}</span>
      </span>
    </>
  );
  // Each card opens the flow the app has, in Simple's frame (spec ui-perf/simple-flows), else it
  // is Coming soon.
  switch (card.way) {
    case "ugc":
      return (
        <Link to="/teams/$teamId/brands/$brandId/simple/ugc-video-ad" params={params} className={classes.card}>
          {body}
        </Link>
      );
    case "image-ad":
      return (
        <Link to="/teams/$teamId/brands/$brandId/simple/image-ad" params={params} className={classes.card}>
          {body}
        </Link>
      );
    case "product-video":
      return (
        <Link
          to="/teams/$teamId/brands/$brandId/simple/make"
          params={params}
          search={{ kind: "video" }}
          className={classes.card}
        >
          {body}
        </Link>
      );
    case "soon":
      return (
        <SoonControl block className={classes.card} label={card.title}>
          {body}
        </SoonControl>
      );
  }
}

/**
 * The prototype's Templates row: its four (Pro Home's pick, by id), of those Simple can run, each
 * opening its own form in the guided page; All templates, the guided page's list (spec
 * ui-perf/simple-home-rows).
 */
function HomeTemplates(props: { teamId: string; brandId: string; usable: (template: TemplateCard) => boolean }) {
  const { teamId, brandId, usable } = props;
  // The guided page's own read, so the two share it; its covers are signed URLs, read again before
  // they expire, as Pro Home's row reads them.
  const templates = useListTemplates(teamId, TEMPLATE_READ, {
    query: freshUrls((page) => page.urls_expire_at),
  });
  const four = templates.data && featuredTemplates(templates.data.items.filter((each) => each.available && usable(each)));
  if (four?.length === 0) return null;
  const params = { teamId, brandId };
  return (
    <section className={classes.section} aria-labelledby="simple-templates">
      <div className={classes.sectionHead}>
        <h2 id="simple-templates" className={classes.sectionTitle}>
          Templates
        </h2>
        <Link
          to="/teams/$teamId/brands/$brandId/simple/make"
          params={params}
          search={{ kind: "ad" }}
          className={home.subtle}
        >
          All templates <ArrowRight size={14} aria-hidden />
        </Link>
      </div>
      {templates.isError ? <ErrorAlert error={templates.error} /> : null}
      <div className={classes.templates}>
        {four === undefined
          ? [0, 1, 2, 3].map((each) => <Skeleton key={each} className={classes.cardSkeleton} />)
          : four.map((template) => (
              <Link
                key={template.id}
                to="/teams/$teamId/brands/$brandId/simple/make"
                params={params}
                search={{ kind: "ad", template: template.id }}
                className={classes.card}
              >
                {/* Its preview plays, as Pro's template cards do (spec ui-perf/template-card-previews). */}
                <span className={classes.cardArt}>
                  <TemplateCover template={template} className={home.thumbCover} iconClassName={home.thumbIcon} />
                </span>
                <span className={classes.cardBody}>
                  <b className={classes.cardTitle}>{template.title}</b>
                  {template.description === null ? null : (
                    <span className={classes.cardLine}>{template.description}</span>
                  )}
                </span>
              </Link>
            ))}
      </div>
    </section>
  );
}

/** The brand's newest jobs, Pro Home's Continue read (its key), shared with Getting started. */
const WORK_READ = 20;

/**
 * The prototype's Continue: the person's own work in the brand with something to show, running
 * first, five at most (Pro Home's rule); a result opens its detail. Shown only once there is some
 * (spec ui-perf/simple-home-rows).
 */
function Continue({ teamId, brandId }: { teamId: string; brandId: string }) {
  const [open, setOpen] = useState<string | null>(null);
  const userId = useQuery({ ...accessScopeQuery(), select: (scope) => scope.user_id }).data;
  const jobs = useJobList(teamId, { status: "all", brandId, pageSize: WORK_READ }, useStreamStatus());
  const catalog = useGetCatalog().data;
  const work = jobs.data && continueJobs(jobs.data.pages.flatMap((page) => page.items), userId);
  if (work === undefined || work.length === 0) return jobs.isError ? <ErrorAlert error={jobs.error} /> : null;
  return (
    <section className={classes.section} aria-labelledby="simple-continue">
      <div className={classes.sectionHead}>
        <h2 id="simple-continue" className={classes.sectionTitle}>
          Continue
        </h2>
        {/* Simple's Library: everything made (spec ui-perf/simple-library). */}
        <Link to="/teams/$teamId/brands/$brandId/simple/library" params={{ teamId, brandId }} className={home.subtle}>
          Library <ArrowRight size={14} aria-hidden />
        </Link>
      </div>
      <div className={classes.cards}>
        {work.map((job) => (
          <ResultCard
            key={job.id}
            job={job}
            model={modelTitle(catalog, job)}
            onLost={() => void jobs.refetch()}
            onOpen={() => setOpen(job.id)}
          />
        ))}
      </div>
      {open === null ? null : <GenerationDetailModal teamId={teamId} jobId={open} onClose={() => setOpen(null)} />}
    </section>
  );
}

/**
 * Simple's Home (spec ui-perf/simple-home): the Created AI Simple prototype's greeting, its set of
 * five ads, and its five single ads, each opening the flow the app has.
 */
export function SimpleHome({ usable }: { usable: (template: TemplateCard) => boolean }) {
  const { teamId, brandId } = useParams({ from: ROUTE });
  const pictures: Pictures = {
    talking: useOwnPictures(teamId, brandId, TALKING_TYPES),
    adStatic: useOwnPictures(teamId, brandId, AD_STATIC_TYPES),
    productVideo: useOwnPictures(teamId, brandId, PRODUCT_VIDEO_TYPES),
  };
  // A Coming soon card has no kind of its own: its icon.
  const own: Readonly<Record<Way, Own | undefined>> = {
    ugc: pictures.talking,
    "image-ad": pictures.adStatic,
    "product-video": pictures.productVideo,
    soon: undefined,
  };
  return (
    <div className={classes.home}>
      <Greeting teamId={teamId} brandId={brandId} />
      <StartHere teamId={teamId} brandId={brandId} pictures={pictures} />
      <section className={classes.section} aria-labelledby="simple-one-ad">
        <div className={classes.sectionHead}>
          <h2 id="simple-one-ad" className={classes.sectionTitle}>
            Or make one ad
          </h2>
          {/* What can be made: the guided page until the picker comes. */}
          <Link to="/teams/$teamId/brands/$brandId/simple/make" params={{ teamId, brandId }} className={home.subtle}>
            Everything you can make <ArrowRight size={14} aria-hidden />
          </Link>
        </div>
        <div className={classes.cards}>
          {ONE_AD.map((card) => (
            <Card key={card.title} card={card} own={own[card.way]} teamId={teamId} brandId={brandId} />
          ))}
        </div>
      </section>
      <HomeTemplates teamId={teamId} brandId={brandId} usable={usable} />
      {/* A brand's own: a result open over one brand's Home never shows over another's. */}
      <Continue key={`${teamId} ${brandId}`} teamId={teamId} brandId={brandId} />
    </div>
  );
}
